import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Typography } from '@mui/material';
import { AxiosResponse } from 'axios';
import { useMutation, useQuery } from 'react-query';
import axiosClient from '../../../../../../lib/axios';
import { enqueueSnackbar } from 'notistack';
import { useMemo, useState } from 'react';

export default function DialogAcompanharRelatorio({ relatorioId, open, onClose }: DialogAcompanharRelatorioProps) {

    const [isProcessando, setIsProcessando] = useState(true);

    // Querys e Mutations
    const { data: verificarRelatorio, isLoading: isLoadingRelatorio, error } = useQuery<AxiosResponse<RelatorioStatusResponse>, Error>({
        queryKey: ["verificar-relatorio-recebimentos", relatorioId],
        queryFn: () => axiosClient.get(`/financeiro/contas-receber/verificar-relatorio-recebimento-baixa/${relatorioId}`),
        onSuccess: (response) => {
            const data = response.data;

            if (data.arquivo_url) {
                enqueueSnackbar('Relatório baixado!', { variant: 'success' });
                baixarArquivo(data.arquivo_url);
                onClose();
            }

            if (data.status === 'processando') {
                setIsProcessando(true);
            } else {
                setIsProcessando(false);

                if (data.arquivo_url) {
                    enqueueSnackbar('Relatório gerado com sucesso!', { variant: 'success' });
                    baixarArquivo(data.arquivo_url);
                    onClose();
                } else if (data.mensagem_erro) {
                    enqueueSnackbar(`Erro ao gerar relatório: ${data.mensagem_erro}`, { variant: 'error' });
                }
            }
        },
        onError: (error) => {
            console.error("Erro ao verificar relatório:", error);
            enqueueSnackbar('Erro ao verificar status do relatório', { variant: 'error' });
        },
        enabled: open && relatorioId !== null,
        refetchInterval: isProcessando ? 4000 : false,
        cacheTime: 0,
        staleTime: 0
    })

    async function baixarArquivo(url: string) {
        const response = await axiosClient.get(url, { responseType: 'blob' });

        const blob = new Blob([response.data], {
            type: response.headers['content-type']
        });

        const tipe = response.headers['content-type'];
        if (tipe === 'application/pdf') {
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.setAttribute('download', 'relatorio.pdf');
            link.click();
            return;
        }

        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.setAttribute('download', 'relatorio.csv');
        link.click();
    }

    const relatorioData = verificarRelatorio?.data;

    const { mutate: tentarNovamente, isLoading } = useMutation({
        mutationKey: "tentar-novamente-relatorio-recebimentos",
        mutationFn: () =>
            axiosClient.post("/financeiro/contas-receber/gerar-recebimento-baixa", {
                tentar_novamente: true,
                relatorio_id: relatorioId
            }),
        onSuccess: (data) => {
            setIsProcessando(true);
            enqueueSnackbar(`Reprocessando: ${data.data.relatorio_id}`, { variant: 'success' })
        },
        onError: (error) => {
            console.error("Erro ao reprocessar o relatório:", error);
        },
    });
    //

    const isError = useMemo(() => {
        return relatorioData?.status === 'erro' || !!relatorioData?.mensagem_erro;
    }, [relatorioData]);

    const handleTentarNovamente = () => {
        tentarNovamente();
    }
    return (
        <Dialog open={open} maxWidth="sm" fullWidth onClose={onClose}>
            <DialogTitle>
                <Typography variant="h6" fontWeight="bold">
                    Acompanhamento do Relatório
                </Typography>
            </DialogTitle>

            <DialogContent dividers sx={{ py: 3 }}>
                <Box sx={{ minHeight: 120, display: "flex", flexDirection: "column", gap: 2 }}>

                    {isLoadingRelatorio && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <CircularProgress size={26} />
                            <Typography>Verificando o status do relatório...</Typography>
                        </Box>
                    )}

                    {relatorioData && (
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 6 }}>
                                <Typography variant="body2" color="text.secondary">
                                    ID do relatório
                                </Typography>
                                <Typography variant="h6">{relatorioId}</Typography>
                            </Grid>

                            <Grid size={{ xs: 6 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Status atual
                                </Typography>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        textTransform: "capitalize",
                                        color:
                                            relatorioData.status === "processando"
                                                ? "primary.main"
                                                : relatorioData.status === "erro"
                                                    ? "error.main"
                                                    : "success.main",
                                    }}
                                >
                                    {relatorioData.status}
                                </Typography>
                            </Grid>

                            {relatorioData.status === "processando" && (
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1.5,
                                        mt: 1,
                                        bgcolor: "#f5f9ff",
                                        borderRadius: 1,
                                        p: 2,
                                    }}
                                >
                                    <CircularProgress size={20} />
                                    <Typography color="text.secondary">
                                        Seu relatório está sendo gerado. Isso pode levar alguns segundos.
                                    </Typography>
                                </Box>
                            )}

                            {relatorioData.mensagem_erro && (
                                <Box
                                    sx={{
                                        mt: 2,
                                        p: 2,
                                        bgcolor: "#ffebee",
                                        borderRadius: 1,
                                        border: "1px solid #ffcdd2",
                                    }}
                                >
                                    <Typography color="error" fontWeight="bold">
                                        Ocorreu um erro
                                    </Typography>
                                    <Typography variant="body2" color="error">
                                        {relatorioData.mensagem_erro}
                                    </Typography>
                                </Box>
                            )}
                        </Grid>
                    )}

                    {error && (
                        <Typography color="error">
                            Não foi possível verificar o status do relatório.
                        </Typography>
                    )}
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button disabled={isLoading} onClick={onClose}>
                    Fechar
                </Button>

                {isError && (
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleTentarNovamente}
                        disabled={isLoading}
                    >
                        Tentar novamente
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}

interface DialogAcompanharRelatorioProps {
    relatorioId: number;
    open: boolean;
    onClose: () => void;
}

interface RelatorioStatusResponse {
    status: string;
    arquivo_url: string | null;
    mensagem_erro: string | null;
}
